type AbortSignalAny = typeof AbortSignal & {
  any?: (signals: AbortSignal[]) => AbortSignal;
};

export function mergeAbortSignals(
  ...sources: AbortSignal[]
): { signal: AbortSignal; dispose: () => void } {
  const active = sources.filter(Boolean);
  if (active.length === 0) {
    return { signal: new AbortController().signal, dispose: () => undefined };
  }
  if (active.length === 1) {
    return { signal: active[0], dispose: () => undefined };
  }

  const abortSignalAny = (AbortSignal as AbortSignalAny).any;
  if (typeof abortSignalAny === 'function') {
    return {
      signal: abortSignalAny(active),
      dispose: () => undefined,
    };
  }

  const controller = new AbortController();
  const disposers: Array<() => void> = [];

  // Forward the reason, so e.g. a timeout is not mistaken for a user abort.
  active.forEach((source) => {
    if (source.aborted) {
      controller.abort(source.reason);
      return;
    }
    const onAbort = (): void => controller.abort(source.reason);
    source.addEventListener('abort', onAbort);
    disposers.push(() => source.removeEventListener('abort', onAbort));
  });

  if (controller.signal.aborted) {
    disposers.forEach((dispose) => dispose());
  }

  return {
    signal: controller.signal,
    dispose: () => disposers.forEach((dispose) => dispose()),
  };
}

/**
 * Aborts with a `TimeoutError` after `timeoutMs`, like `AbortSignal.timeout`,
 * but the timer can be released once the request settles.
 */
export function createTimeoutSignal(timeoutMs: number): {
  signal: AbortSignal;
  dispose: () => void;
} {
  const controller = new AbortController();
  const timer = setTimeout(
    () =>
      controller.abort(
        new DOMException(`Timed out after ${timeoutMs} ms`, 'TimeoutError')
      ),
    timeoutMs
  );
  return { signal: controller.signal, dispose: () => clearTimeout(timer) };
}
